import { useState, useEffect, useCallback } from "react";
import { Navbar } from "../components/Navbar";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { API_URL, API_BASE_URL } from "../config/api";
import { 
  Star, 
  TrendingUp, 
  TrendingDown, 
  Users, 
  AlertTriangle, 
  CheckCircle,
  Search,
  Filter,
  RefreshCw,
  ThumbsUp,
  ThumbsDown,
  Meh,
  User,
  Calendar,
  FileText,
  ChevronDown,
  ChevronUp,
  Loader2
} from "lucide-react";


const RATING_LABELS = {
  1: { label: "Very Unsatisfied", color: "bg-red-500", textColor: "text-red-600" },
  2: { label: "Unsatisfied", color: "bg-orange-500", textColor: "text-orange-600" },
  3: { label: "Neutral", color: "bg-yellow-500", textColor: "text-yellow-600" },
  4: { label: "Satisfied", color: "bg-lime-500", textColor: "text-lime-600" },
  5: { label: "Very Satisfied", color: "bg-green-500", textColor: "text-green-600" },
};

function StarRating({ rating }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`h-4 w-4 ${star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-slate-300'}`}
        />
      ))}
    </div>
  );
}

function StaffCard({ stats, onViewDetails }) {
  const avgRating = stats.average_rating || 0;
  const ratingColor = avgRating >= 4 ? "text-green-600" : avgRating >= 3 ? "text-yellow-600" : "text-red-600";
  
  return (
    <Card className="hover:shadow-lg transition-shadow">
      <CardContent className="pt-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-slate-200 flex items-center justify-center">
              <User className="h-6 w-6 text-slate-500" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-800">{stats.staff_name}</h3>
              <p className="text-sm text-slate-500">{stats.staff_email}</p>
            </div>
          </div>
          <div className={`text-2xl font-bold ${ratingColor}`}>
            {avgRating.toFixed(1)}
          </div>
        </div>

        <div className="flex items-center justify-between mb-4">
          <StarRating rating={Math.round(avgRating)} />
          <span className="text-sm text-slate-500">{stats.total_reviews} reviews</span>
        </div>

        {/* Rating Distribution */}
        <div className="space-y-1 mb-4">
          {[5, 4, 3, 2, 1].map((r) => {
            const count = stats.ratings?.[r] || 0;
            const pct = stats.total_reviews > 0 ? (count / stats.total_reviews) * 100 : 0;
            return (
              <div key={r} className="flex items-center gap-2 text-xs">
                <span className="w-3 text-slate-500">{r}</span>
                <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div 
                    className={`h-full ${RATING_LABELS[r].color}`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <span className="w-6 text-slate-500">{count}</span>
              </div>
            );
          })}
        </div>

        {stats.low_ratings_count > 0 && (
          <div className="flex items-center gap-2 text-orange-600 text-sm mb-4">
            <AlertTriangle className="h-4 w-4" />
            <span>{stats.low_ratings_count} low ratings need attention</span>
          </div>
        )}

        <Button variant="outline" className="w-full" onClick={() => onViewDetails(stats)}>
          View Details
        </Button>
      </CardContent>
    </Card>
  );
}

function ReviewItem({ review, expanded, onToggle }) {
  const ratingInfo = RATING_LABELS[review.rating] || RATING_LABELS[3];
  
  return (
    <div className={`border rounded-lg p-4 ${review.rating <= 3 ? 'bg-orange-50 border-orange-200' : 'bg-white'}`}>
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full ${ratingInfo.color} flex items-center justify-center text-white font-bold`}>
            {review.rating}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-medium">{review.customer_name}</span>
              <Badge variant={review.rating <= 3 ? "destructive" : "default"} className="text-xs">
                {ratingInfo.label}
              </Badge>
            </div>
            <div className="flex items-center gap-3 text-sm text-slate-500 mt-1">
              <span className="flex items-center gap-1">
                <FileText className="h-3 w-3" />
                {review.pf_number}
              </span>
              <span className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                {new Date(review.created_at).toLocaleDateString()}
              </span>
            </div>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={onToggle}>
          {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </Button>
      </div>

      {expanded && (
        <div className="mt-4 pt-4 border-t space-y-3">
          <div className="flex items-center gap-2">
            <User className="h-4 w-4 text-slate-400" />
            <span className="text-sm">Staff: <strong>{review.staff_name}</strong></span>
          </div>
          
          {review.reason && (
            <div className="bg-white rounded-lg p-3 border">
              <Label className="text-xs text-slate-500 block mb-1">Reason for Rating</Label>
              <p className="text-sm">{review.reason}</p>
            </div>
          )}
          
          {review.additional_feedback && (
            <div className="bg-white rounded-lg p-3 border">
              <Label className="text-xs text-slate-500 block mb-1">Additional Feedback</Label>
              <p className="text-sm">{review.additional_feedback}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const Label = ({ children, className }) => (
  <span className={`font-medium ${className || ''}`}>{children}</span>
);

export default function SatisfactionReviewsPage() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState([]);
  const [staffStats, setStaffStats] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [expandedReview, setExpandedReview] = useState(null);
  const [filterRating, setFilterRating] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const fetchReviews = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterRating) {
        params.max_rating = parseInt(filterRating);
      }
      const response = await axios.get(`${API_URL}/service-satisfaction/reviews`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setReviews(response.data.reviews || []);
      setStaffStats(response.data.staff_stats || []);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("Access denied. This page is only for HR and CEO.");
      } else {
        toast.error("Failed to load reviews");
      }
      console.error("Failed to fetch reviews:", error);
    } finally {
      setLoading(false);
    }
  }, [token, filterRating]);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  // Calculate overall stats
  const overallStats = {
    total: reviews.length,
    avgRating: reviews.length > 0 
      ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1) 
      : 0,
    lowRatings: reviews.filter(r => r.rating <= 3).length,
    highRatings: reviews.filter(r => r.rating >= 4).length,
  };

  const filteredReviews = reviews.filter(r => {
    if (selectedStaff && r.staff_id !== selectedStaff.staff_id) return false;
    if (searchTerm && !r.customer_name.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !r.pf_number.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="flex items-center justify-center h-[calc(100vh-64px)]">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      
      {/* Header */}
      <div className="bg-gradient-to-r from-[#003366] to-[#004488] text-white px-6 py-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <Star className="h-6 w-6" />
                Service Satisfaction Reviews
              </h1>
              <p className="text-blue-200 text-sm mt-1">
                Customer feedback on staff performance (HR & CEO Access Only)
              </p>
            </div>
            <Button variant="outline" onClick={fetchReviews} className="text-white border-white/30 hover:bg-white/10">
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Overall Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Reviews</p>
                  <p className="text-3xl font-bold">{overallStats.total}</p>
                </div>
                <Users className="h-10 w-10 text-blue-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Average Rating</p>
                  <p className="text-3xl font-bold flex items-center gap-2">
                    {overallStats.avgRating}
                    <Star className="h-6 w-6 fill-yellow-400 text-yellow-400" />
                  </p>
                </div>
                <TrendingUp className="h-10 w-10 text-green-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Satisfied (4-5)</p>
                  <p className="text-3xl font-bold text-green-600">{overallStats.highRatings}</p>
                </div>
                <ThumbsUp className="h-10 w-10 text-green-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card className={overallStats.lowRatings > 0 ? "border-orange-300 bg-orange-50" : ""}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Needs Attention (1-3)</p>
                  <p className="text-3xl font-bold text-orange-600">{overallStats.lowRatings}</p>
                </div>
                <AlertTriangle className="h-10 w-10 text-orange-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Staff Performance Cards */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Staff Performance</CardTitle>
            <CardDescription>Click on a staff member to filter reviews</CardDescription>
          </CardHeader>
          <CardContent>
            {staffStats.length === 0 ? (
              <p className="text-slate-500 text-center py-8">No reviews yet</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {staffStats.map((stats, idx) => (
                  <StaffCard 
                    key={idx} 
                    stats={stats}
                    onViewDetails={(s) => setSelectedStaff(selectedStaff?.staff_email === s.staff_email ? null : s)}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Reviews List */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>
                  {selectedStaff ? `Reviews for ${selectedStaff.staff_name}` : 'All Reviews'}
                </CardTitle>
                <CardDescription>
                  {filteredReviews.length} reviews
                  {selectedStaff && (
                    <Button variant="link" className="p-0 h-auto ml-2" onClick={() => setSelectedStaff(null)}>
                      Clear filter
                    </Button>
                  )}
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                  <Input 
                    placeholder="Search..." 
                    className="pl-9 w-48"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <select 
                  className="border rounded-md px-3 py-2 text-sm"
                  value={filterRating}
                  onChange={(e) => setFilterRating(e.target.value)}
                >
                  <option value="">All Ratings</option>
                  <option value="3">Low (1-3)</option>
                  <option value="2">Critical (1-2)</option>
                </select>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {filteredReviews.length === 0 ? (
              <p className="text-slate-500 text-center py-8">No reviews found</p>
            ) : (
              <div className="space-y-3">
                {filteredReviews.map((review) => (
                  <ReviewItem 
                    key={review.id}
                    review={review}
                    expanded={expandedReview === review.id}
                    onToggle={() => setExpandedReview(expandedReview === review.id ? null : review.id)}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
